import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import format from 'date-fns/format';
import Heading from '../../../generic-components/Heading';
import { ReactComponent as IconVSuccessSign } from '../../../assets/icons/svg/colored-icons/ic-colored-success.svg';
import TransferSuccessBox, { ISuccessBoxRow } from '../components/TransferSuccessBox';
import { refactoredTimeAndDate } from '../../../helpers/timeFormatter.helper';
import { DATE_FORMAT } from '../../../constants/general';
import SuccessDownloadButton from '../components/SuccessDownloadButton';
import { takeSnapshot } from '../../../services/snapshot.service';
import TransferSuccessPopup from '../../BankTransfer/components/TransferSuccessPopup';
import SendMoneyWizardFooter from '../components/SendMoneyWizardFooter';
import Button from '../../../generic-components/Button';
import { useAccountSummaryQuery } from '../../../hooks/query/useAccountSummaryQuery';
import { trackMixPanel } from '../../../services/mixpanel.service';
import { SendMoneyContext } from './SendMoney';
import { convertValueToUSD } from '../../../helpers/currency.helper';

const STYLES = {
  WRAPPER: 'flex flex-col items-center justify-center w-full h-[calc(100vh-160px)] overflow-y-auto mt-[58px]',
  MAIN: 'w-full flex flex-col h-full items-center bg-bw02',
  TITLE_WRAPPER: 'w-[390px] mt-[22px]',
  TITLE: 'font-metricRMedium text-[18px] text-bw08 leading-6 mt-[10px] mb-[5px] text-center',
  TITLE_ICON: 'm-auto w-[68px] h-[68px] mb-[10px]',
  SUBTITLE: 'font-metricR text-bw07 text-md leading-6 mb-[16px] text-center',
};

interface ITransferSuccessPage {
  processTime: string;
  rowsData: ISuccessBoxRow[];
  onMakeAnotherTransferClick?: () => void;
}
const TransferSuccessPage = ({ rowsData, processTime, onMakeAnotherTransferClick }: ITransferSuccessPage) => {
  const { t } = useTranslation();
  const { refetch: updateSummary, isLoading, isRefetching } = useAccountSummaryQuery();
  const navigate = useNavigate();
  const [isSnapshotMode, setIsSnapshotMode] = useState<boolean>(false);
  const successContent = <TransferSuccessBox rowsData={rowsData} processTime={refactoredTimeAndDate(processTime, 'SEND-MONEY', DATE_FORMAT.LETTERS)} />;
  const { amount, chosenFee } = useContext(SendMoneyContext);
  const handleDownloadClick = () => {
    setIsSnapshotMode(true);
    setTimeout(() => {
      takeSnapshot('sendMoneySuccessPopup', true)
        .then(url => {
          const elLink = document.createElement('a');
          elLink.setAttribute('href', url as string);
          elLink.setAttribute('download', `sendMoney-${format(new Date(), 'MMM dd, yyyy')}`);
          elLink.click();
          setIsSnapshotMode(false);
        });
    }, 150);
    trackMixPanel('Act on Move Money Confirmation Screen', {
      Flow: 'plaid transfer out',
      Action: 'Download',
    });
  };
  useEffect(() => {
    trackMixPanel('View Move Money Confirmation Screen', {
      Flow: 'plaid transfer out',
      Amount: amount,
      FeePaid: convertValueToUSD(chosenFee?.fee, 2) || '',
      EstimatedArrival: chosenFee?.arrivalDate || '',
    });
    updateSummary();
  }, []);
  const handleOnMakeAnotherTransferClick = () => {
    trackMixPanel('Act on Move Money Confirmation Screen', {
      Flow: 'plaid transfer out',
      Action: 'Send Another',
    });
    onMakeAnotherTransferClick?.();
  };
  const handleGoToDashboardClick = () => {
    trackMixPanel('Act on Move Money Confirmation Screen', {
      Flow: 'plaid transfer out',
      Action: 'Done',
    });
    setTimeout(() => {
      navigate('/');
    }, 300);
  };

  const shouldDisableButtons = isLoading || isRefetching;

  return (
    <div className={STYLES.WRAPPER}>
      <div className={STYLES.MAIN}>
        {
          isSnapshotMode
          && (
            <TransferSuccessPopup featureName='sendMoney'>
              {successContent}
            </TransferSuccessPopup>
          )
        }
        <div className={STYLES.TITLE_WRAPPER}>
          <IconVSuccessSign className={STYLES.TITLE_ICON} data-testid='TransferSuccessPage-TitleIcon' />
          <Heading testId='TransferSuccessPage-Title' title={t('SUCCESS_PAGE.TITLE')} className={STYLES.TITLE} />
          <Heading
            testId='TransferSuccessPage-Subtitle'
            title={t('SUCCESS_PAGE.SUBTITLE')}
            className={STYLES.SUBTITLE}
          />
        </div>
        {successContent}
        <SuccessDownloadButton onClick={handleDownloadClick} />
      </div>
      <SendMoneyWizardFooter
        title={t('SUCCESS_PAGE.GO_TO_DASHBOARD')}
        onClick={handleGoToDashboardClick}
        className='w-[226px] h-[54px]'
        isLoading={shouldDisableButtons}
        secondaryButton={(
          <Button
            onClick={handleOnMakeAnotherTransferClick}
            title={t('SUCCESS_PAGE.MAKE_ANOTHER_TRANSFER')}
            className='w-[226px] h-[54px] text-center rounded border-action03 border-[1px] flex justify-center items-center mr-[12px] text-action03 font-metricRMedium text-md'
            id=''
            testId='MakeAnotherTransferCTA'
            isLoading={shouldDisableButtons}
          />
        )}
      />
    </div>
  );
};

export default TransferSuccessPage;
